import { useState, useEffect } from "react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { useAuth } from "../../App";
import { statusLabels, fineTypeLabels } from "../../lib/constants";

// ==================== VEHICLES PAGE ====================
const VehiclesPage = () => {
  const [vehicles, setVehicles] = useState([]);
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [kmLogs, setKmLogs] = useState({ logs: [], summary: {} });
  const [fines, setFines] = useState({ fines: [], summary: {} });
  const [loading, setLoading] = useState(true);
  const [showKmModal, setShowKmModal] = useState(false);
  const [showFineModal, setShowFineModal] = useState(false);
  const [activeTab, setActiveTab] = useState('info');
  const { user } = useAuth();
  const isAdmin = ['super_admin', 'tenant_admin', 'hr_manager'].includes(user?.role);

  const fetchVehicles = async () => {
    try {
      const response = await api.get('/portal/vehicles');
      setVehicles(response.data);
      if (response.data.length > 0) {
        setSelectedVehicle(response.data[0]);
      }
    } catch (error) {
      console.error('Error fetching vehicles:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchVehicles(); }, []);

  useEffect(() => {
    if (selectedVehicle) {
      const fetchDetails = async () => {
        try {
          const [logsResp, finesResp] = await Promise.all([
            api.get(`/portal/vehicles/${selectedVehicle.vehicle_id}/km-logs`),
            api.get(`/portal/vehicles/${selectedVehicle.vehicle_id}/fines`)
          ]);
          setKmLogs(logsResp.data);
          setFines(finesResp.data);
        } catch (error) {
          console.error('Error fetching vehicle details:', error);
        }
      };
      fetchDetails();
    }
  }, [selectedVehicle]);

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div></div>;
  }

  if (vehicles.length === 0) {
    return (
      <div data-testid="vehicles-page" className="space-y-6">
        <h1 className="text-2xl md:text-3xl font-bold font-['Plus_Jakarta_Sans']">Gestão de Viaturas</h1>
        <div className="text-center py-12 bg-card rounded-xl border"><p className="text-muted-foreground">Sem viatura atribuída</p></div>
      </div>
    );
  }

  return (
    <div data-testid="vehicles-page" className="space-y-6 animate-fade-in">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold font-['Plus_Jakarta_Sans']">Gestão de Viaturas</h1>
          <p className="text-muted-foreground">Gerir viatura, km e coimas</p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => setShowKmModal(true)} className="px-4 py-2 rounded-lg bg-primary text-primary-foreground font-medium hover:bg-primary/90 flex items-center gap-2" data-testid="add-km-btn">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg>
            Registar Km
          </button>
          {isAdmin && (
            <button onClick={() => setShowFineModal(true)} className="px-4 py-2 rounded-lg border hover:bg-muted flex items-center gap-2" data-testid="add-fine-btn">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" /></svg>
              Registar Coima
            </button>
          )}
        </div>
      </div>

      {vehicles.length > 1 && (
        <div className="flex gap-2 overflow-x-auto pb-2">
          {vehicles.map(v => (
            <button key={v.vehicle_id} onClick={() => setSelectedVehicle(v)} className={`px-4 py-2 rounded-lg border whitespace-nowrap transition-all ${selectedVehicle?.vehicle_id === v.vehicle_id ? 'border-primary bg-primary/10' : 'hover:border-primary/50'}`}>
              {v.license_plate}
            </button>
          ))}
        </div>
      )}

      {selectedVehicle && (
        <>
          <div className="rounded-xl border bg-card p-6">
            <div className="flex items-start gap-6">
              <div className="w-20 h-20 rounded-xl bg-gradient-to-br from-slate-700 to-slate-900 flex items-center justify-center flex-shrink-0">
                <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h8m-8 0a2 2 0 110-4 2 2 0 010 4zm8 0a2 2 0 110-4 2 2 0 010 4zM3 13h18M5 17a2 2 0 01-2-2V9a2 2 0 012-2h14a2 2 0 012 2v6a2 2 0 01-2 2m-12 0h.01M17 17h.01" />
                </svg>
              </div>
              <div className="flex-1 grid md:grid-cols-4 gap-4">
                <div><p className="text-sm text-muted-foreground">Matrícula</p><p className="text-2xl font-bold">{selectedVehicle.license_plate}</p></div>
                <div><p className="text-sm text-muted-foreground">Veículo</p><p className="font-medium">{selectedVehicle.brand} {selectedVehicle.model}</p></div>
                <div><p className="text-sm text-muted-foreground">Km Atual</p><p className="font-medium text-primary">{selectedVehicle.current_km?.toLocaleString('pt-PT')} km</p></div>
                <div><p className="text-sm text-muted-foreground">Combustível</p><p className="font-medium capitalize">{selectedVehicle.fuel_type}</p></div>
              </div>
            </div>
          </div>

          <div className="flex gap-2 border-b">
            <button onClick={() => setActiveTab('info')} className={`px-4 py-2 font-medium border-b-2 transition-colors ${activeTab === 'info' ? 'border-primary text-primary' : 'border-transparent text-muted-foreground'}`}>Informações</button>
            <button onClick={() => setActiveTab('km')} className={`px-4 py-2 font-medium border-b-2 transition-colors ${activeTab === 'km' ? 'border-primary text-primary' : 'border-transparent text-muted-foreground'}`}>Registo Km ({kmLogs.logs?.length || 0})</button>
            <button onClick={() => setActiveTab('fines')} className={`px-4 py-2 font-medium border-b-2 transition-colors ${activeTab === 'fines' ? 'border-primary text-primary' : 'border-transparent text-muted-foreground'}`}>Coimas ({fines.fines?.length || 0})</button>
          </div>

          {activeTab === 'info' && (
            <div className="grid md:grid-cols-2 gap-6">
              <div className="rounded-xl border bg-card p-6">
                <h3 className="font-semibold mb-4">Detalhes do Veículo</h3>
                <dl className="space-y-3">
                  <div className="flex justify-between"><dt className="text-muted-foreground">Ano</dt><dd>{selectedVehicle.year || '-'}</dd></div>
                  <div className="flex justify-between"><dt className="text-muted-foreground">Km Inicial</dt><dd>{selectedVehicle.initial_km?.toLocaleString('pt-PT')} km</dd></div>
                  <div className="flex justify-between"><dt className="text-muted-foreground">Próxima Revisão</dt><dd>{selectedVehicle.next_service_km?.toLocaleString('pt-PT')} km</dd></div>
                  <div className="flex justify-between"><dt className="text-muted-foreground">IPO</dt><dd>{selectedVehicle.inspection_date || '-'}</dd></div>
                </dl>
              </div>
              <div className="rounded-xl border bg-card p-6">
                <h3 className="font-semibold mb-4">Seguro</h3>
                <dl className="space-y-3">
                  <div className="flex justify-between"><dt className="text-muted-foreground">Seguradora</dt><dd>{selectedVehicle.insurance_company || '-'}</dd></div>
                  <div className="flex justify-between"><dt className="text-muted-foreground">Apólice</dt><dd>{selectedVehicle.insurance_policy || '-'}</dd></div>
                  <div className="flex justify-between"><dt className="text-muted-foreground">Validade</dt><dd className={selectedVehicle.insurance_expiry && new Date(selectedVehicle.insurance_expiry) < new Date() ? 'text-red-500 font-medium' : ''}>{selectedVehicle.insurance_expiry || '-'}</dd></div>
                </dl>
              </div>
            </div>
          )}

          {activeTab === 'km' && (
            <div className="space-y-4">
              <div className="grid grid-cols-3 gap-4">
                <div className="rounded-xl border bg-card p-4 text-center">
                  <p className="text-2xl font-bold text-primary">{kmLogs.summary?.total_km?.toLocaleString('pt-PT') || 0}</p>
                  <p className="text-sm text-muted-foreground">Km Registados</p>
                </div>
                <div className="rounded-xl border bg-card p-4 text-center">
                  <p className="text-2xl font-bold">{kmLogs.summary?.total_entries || 0}</p>
                  <p className="text-sm text-muted-foreground">Registos</p>
                </div>
                <div className="rounded-xl border bg-card p-4 text-center">
                  <p className="text-2xl font-bold">{kmLogs.summary?.total_fuel_expense?.toLocaleString('pt-PT') || 0}€</p>
                  <p className="text-sm text-muted-foreground">Combustível</p>
                </div>
              </div>
              
              {kmLogs.logs?.length > 0 ? (
                <div className="rounded-xl border bg-card overflow-hidden">
                  <table className="data-table">
                    <thead><tr><th>Data</th><th>Km Início</th><th>Km Fim</th><th>Percorridos</th><th>Finalidade</th><th>Destino</th></tr></thead>
                    <tbody>
                      {kmLogs.logs.map((log) => (
                        <tr key={log.log_id}>
                          <td>{log.date}</td>
                          <td>{log.start_km?.toLocaleString('pt-PT')}</td>
                          <td>{log.end_km?.toLocaleString('pt-PT')}</td>
                          <td className="font-medium text-primary">{log.km_traveled?.toLocaleString('pt-PT')} km</td>
                          <td className="capitalize">{log.purpose === 'trabalho' ? 'Trabalho' : log.purpose === 'pessoal' ? 'Pessoal' : 'Misto'}</td>
                          <td>{log.destination || '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-8 bg-card rounded-xl border"><p className="text-muted-foreground">Sem registos de km</p></div>
              )}
            </div>
          )}

          {activeTab === 'fines' && (
            <div className="space-y-4">
              <div className="grid grid-cols-3 gap-4">
                <div className="rounded-xl border bg-card p-4 text-center">
                  <p className="text-2xl font-bold">{fines.summary?.total_fines || 0}</p>
                  <p className="text-sm text-muted-foreground">Total Coimas</p>
                </div>
                <div className="rounded-xl border bg-card p-4 text-center">
                  <p className="text-2xl font-bold text-red-500">{fines.summary?.total_amount?.toLocaleString('pt-PT') || 0}€</p>
                  <p className="text-sm text-muted-foreground">Valor Total</p>
                </div>
                <div className="rounded-xl border bg-card p-4 text-center">
                  <p className="text-2xl font-bold text-amber-500">{fines.summary?.pending_amount?.toLocaleString('pt-PT') || 0}€</p>
                  <p className="text-sm text-muted-foreground">Pendente</p>
                </div>
              </div>

              {fines.fines?.length > 0 ? (
                <div className="rounded-xl border bg-card overflow-hidden">
                  <table className="data-table">
                    <thead><tr><th>Data</th><th>Tipo</th><th>Local</th><th>Valor</th><th>Estado</th></tr></thead>
                    <tbody>
                      {fines.fines.map((fine) => (
                        <tr key={fine.fine_id}>
                          <td>{fine.fine_date}</td>
                          <td>{fineTypeLabels[fine.fine_type] || fine.fine_type}</td>
                          <td>{fine.location || '-'}</td>
                          <td className="font-medium text-red-500">{fine.amount?.toLocaleString('pt-PT')}€</td>
                          <td>
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${fine.payment_status === 'pendente' ? 'bg-amber-100 text-amber-700' : 'bg-emerald-100 text-emerald-700'}`}>
                              {statusLabels[fine.payment_status] || fine.payment_status}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-8 bg-card rounded-xl border"><p className="text-muted-foreground">Sem coimas registadas</p></div>
              )}
            </div>
          )}
        </>
      )}

      {showKmModal && selectedVehicle && (
        <AddKmModal vehicleId={selectedVehicle.vehicle_id} currentKm={selectedVehicle.current_km} onClose={() => setShowKmModal(false)} onCreated={() => { setShowKmModal(false); fetchVehicles(); }} />
      )}

      {showFineModal && selectedVehicle && isAdmin && (
        <AddFineModal vehicleId={selectedVehicle.vehicle_id} onClose={() => setShowFineModal(false)} onCreated={() => { setShowFineModal(false); fetchVehicles(); }} />
      )}
    </div>
  );
};

const AddKmModal = ({ vehicleId, currentKm, onClose, onCreated }) => {
  const [formData, setFormData] = useState({ date: new Date().toISOString().split('T')[0], start_km: currentKm || 0, end_km: '', purpose: 'trabalho', destination: '', fuel_expense: '', fuel_liters: '' });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (parseFloat(formData.end_km) <= parseFloat(formData.start_km)) {
      toast.error('Km final deve ser maior que km inicial');
      return;
    }
    setLoading(true);
    try {
      await api.post(`/portal/vehicles/${vehicleId}/km-log`, {
        ...formData,
        start_km: parseFloat(formData.start_km),
        end_km: parseFloat(formData.end_km),
        fuel_expense: formData.fuel_expense ? parseFloat(formData.fuel_expense) : null,
        fuel_liters: formData.fuel_liters ? parseFloat(formData.fuel_liters) : null
      });
      toast.success('Registo de km criado');
      onCreated();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao criar registo');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-card rounded-xl border w-full max-w-md" onClick={e => e.stopPropagation()}>
        <div className="p-6 border-b flex items-center justify-between">
          <h3 className="text-lg font-semibold">Registar Km</h3>
          <button onClick={onClose} className="p-2 hover:bg-muted rounded-lg"><svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg></button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div><label className="block text-sm font-medium mb-1">Data *</label><input type="date" required value={formData.date} onChange={e => setFormData({...formData, date: e.target.value})} className="w-full px-3 py-2 rounded-lg border border-input bg-background" /></div>
          <div className="grid grid-cols-2 gap-4">
            <div><label className="block text-sm font-medium mb-1">Km Início *</label><input type="number" required value={formData.start_km} onChange={e => setFormData({...formData, start_km: e.target.value})} className="w-full px-3 py-2 rounded-lg border border-input bg-background" /></div>
            <div><label className="block text-sm font-medium mb-1">Km Fim *</label><input type="number" required value={formData.end_km} onChange={e => setFormData({...formData, end_km: e.target.value})} className="w-full px-3 py-2 rounded-lg border border-input bg-background" /></div>
          </div>
          <div><label className="block text-sm font-medium mb-1">Finalidade</label>
            <select value={formData.purpose} onChange={e => setFormData({...formData, purpose: e.target.value})} className="w-full px-3 py-2 rounded-lg border border-input bg-background">
              <option value="trabalho">Trabalho</option><option value="pessoal">Pessoal</option><option value="misto">Misto</option>
            </select>
          </div>
          <div><label className="block text-sm font-medium mb-1">Destino</label><input type="text" value={formData.destination} onChange={e => setFormData({...formData, destination: e.target.value})} className="w-full px-3 py-2 rounded-lg border border-input bg-background" placeholder="Ex: Lisboa → Porto" /></div>
          <div className="grid grid-cols-2 gap-4">
            <div><label className="block text-sm font-medium mb-1">Despesa Combustível (€)</label><input type="number" step="0.01" value={formData.fuel_expense} onChange={e => setFormData({...formData, fuel_expense: e.target.value})} className="w-full px-3 py-2 rounded-lg border border-input bg-background" /></div>
            <div><label className="block text-sm font-medium mb-1">Litros</label><input type="number" step="0.01" value={formData.fuel_liters} onChange={e => setFormData({...formData, fuel_liters: e.target.value})} className="w-full px-3 py-2 rounded-lg border border-input bg-background" /></div>
          </div>
          <div className="flex justify-end gap-3 pt-4">
            <button type="button" onClick={onClose} className="px-4 py-2 rounded-lg border hover:bg-muted">Cancelar</button>
            <button type="submit" disabled={loading} className="px-4 py-2 rounded-lg bg-primary text-primary-foreground font-medium disabled:opacity-50">{loading ? 'A guardar...' : 'Guardar'}</button>
          </div>
        </form>
      </div>
    </div>
  );
};

const AddFineModal = ({ vehicleId, onClose, onCreated }) => {
  const [formData, setFormData] = useState({ fine_date: '', fine_type: 'velocidade', location: '', amount: '', points_deducted: 0, notes: '' });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post(`/portal/vehicles/${vehicleId}/fines`, {
        ...formData,
        amount: parseFloat(formData.amount),
        points_deducted: parseInt(formData.points_deducted) || 0
      });
      toast.success('Coima registada');
      onCreated();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao registar coima');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-card rounded-xl border w-full max-w-md" onClick={e => e.stopPropagation()}>
        <div className="p-6 border-b flex items-center justify-between">
          <h3 className="text-lg font-semibold">Registar Coima</h3>
          <button onClick={onClose} className="p-2 hover:bg-muted rounded-lg"><svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg></button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div><label className="block text-sm font-medium mb-1">Data *</label><input type="date" required value={formData.fine_date} onChange={e => setFormData({...formData, fine_date: e.target.value})} className="w-full px-3 py-2 rounded-lg border border-input bg-background" /></div>
          <div><label className="block text-sm font-medium mb-1">Tipo *</label>
            <select value={formData.fine_type} onChange={e => setFormData({...formData, fine_type: e.target.value})} className="w-full px-3 py-2 rounded-lg border border-input bg-background">
              <option value="velocidade">Excesso de Velocidade</option>
              <option value="estacionamento">Estacionamento</option>
              <option value="sinal">Desrespeito de Sinalização</option>
              <option value="alcool">Álcool/Drogas</option>
              <option value="outro">Outro</option>
            </select>
          </div>
          <div><label className="block text-sm font-medium mb-1">Local</label><input type="text" value={formData.location} onChange={e => setFormData({...formData, location: e.target.value})} className="w-full px-3 py-2 rounded-lg border border-input bg-background" placeholder="Ex: A1 - km 45" /></div>
          <div className="grid grid-cols-2 gap-4">
            <div><label className="block text-sm font-medium mb-1">Valor (€) *</label><input type="number" step="0.01" required value={formData.amount} onChange={e => setFormData({...formData, amount: e.target.value})} className="w-full px-3 py-2 rounded-lg border border-input bg-background" /></div>
            <div><label className="block text-sm font-medium mb-1">Pontos</label><input type="number" value={formData.points_deducted} onChange={e => setFormData({...formData, points_deducted: e.target.value})} className="w-full px-3 py-2 rounded-lg border border-input bg-background" /></div>
          </div>
          <div><label className="block text-sm font-medium mb-1">Notas</label><textarea value={formData.notes} onChange={e => setFormData({...formData, notes: e.target.value})} rows={2} className="w-full px-3 py-2 rounded-lg border border-input bg-background resize-none" /></div>
          <div className="flex justify-end gap-3 pt-4">
            <button type="button" onClick={onClose} className="px-4 py-2 rounded-lg border hover:bg-muted">Cancelar</button>
            <button type="submit" disabled={loading} className="px-4 py-2 rounded-lg bg-primary text-primary-foreground font-medium disabled:opacity-50">{loading ? 'A guardar...' : 'Guardar'}</button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default VehiclesPage;
